import {
  Accessibility,
  AlertTriangle,
  ArrowRight,
  Code,
  FileText,
  Image,
  Keyboard,
  Lock,
  Palette,
  Smartphone,
  Volume2,
} from "lucide-react";
import { Link } from "react-router-dom";

const efforts = [
  {
    icon: Code,
    label: "Using semantic HTML to provide meaningful document structure",
  },
  {
    icon: Keyboard,
    label: "Supporting keyboard navigation throughout the interface",
  },
  {
    icon: Palette,
    label: "Maintaining sufficient color contrast ratios for readability",
  },
  {
    icon: Volume2,
    label: "Adding ARIA labels and roles for screen reader compatibility",
  },
  {
    icon: Smartphone,
    label:
      "Building responsive layouts that work across devices and zoom levels",
  },
  {
    icon: Image,
    label: "Providing descriptive alt text for meaningful images",
  },
];

export default function AccessibilityPage() {
  return (
    <main className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <section className="relative pt-32 pb-16 bg-[#09090b]">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-950/20 to-transparent" />
        <div className="container mx-auto px-6 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 text-sm mb-6">
              <Accessibility className="w-4 h-4" />
              Legal
            </div>
            <h1 className="text-4xl md:text-5xl font-bold font-display text-white mb-4">
              Accessibility Statement
            </h1>
            <p className="text-lg text-zinc-400">
              Last Updated: March 18, 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Intro callout */}
          <div className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800 mb-8">
            <div className="border-l-4 border-violet-500 pl-6">
              <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                At Furistic, we believe technology should be usable by everyone.
                We are committed to making our platform and products, including
                RealtyOS, TradeOS, ConformOS, Haulyard, and Foodium, accessible
                to people of all abilities. We target conformance with the Web
                Content Accessibility Guidelines (WCAG) 2.1 at the AA level.
              </p>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            {/* What We Are Doing */}
            <div className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                  <span className="text-sm font-bold text-violet-400">1</span>
                </div>
                <div className="flex items-center gap-3">
                  <Code className="w-5 h-5 text-violet-400" />
                  <h2 className="text-xl md:text-2xl font-bold text-zinc-900 dark:text-white">
                    What We Are Doing
                  </h2>
                </div>
              </div>
              <div className="pl-14">
                <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed mb-4">
                  We are actively working to ensure our websites and
                  applications meet accessibility standards. Our efforts
                  include:
                </p>
                <ul className="space-y-3">
                  {efforts.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.label} className="flex items-start gap-3">
                        <Icon className="w-4 h-4 text-violet-400 flex-shrink-0 mt-1" />
                        <span className="text-zinc-700 dark:text-zinc-300 text-sm">
                          {item.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* Known Limitations */}
            <div className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                  <span className="text-sm font-bold text-violet-400">2</span>
                </div>
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-violet-400" />
                  <h2 className="text-xl md:text-2xl font-bold text-zinc-900 dark:text-white">
                    Known Limitations
                  </h2>
                </div>
              </div>
              <div className="pl-14">
                <div className="border-l-4 border-amber-500 pl-4">
                  <p className="text-zinc-700 dark:text-zinc-300 text-sm leading-relaxed">
                    <strong className="text-amber-600 dark:text-amber-400">
                      We are not fully compliant yet.
                    </strong>{" "}
                    We are working toward WCAG 2.1 AA compliance but have not
                    yet completed a comprehensive accessibility audit across all
                    products. Some areas, particularly complex interactive
                    features, data visualizations, and third-party integrations,
                    may not yet meet all requirements. We are addressing these
                    gaps as part of our ongoing development across RealtyOS,
                    TradeOS, ConformOS, Haulyard, Foodium, and our shared
                    platform services.
                  </p>
                </div>
              </div>
            </div>

            {/* Report an Issue */}
            <div className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                  <span className="text-sm font-bold text-violet-400">3</span>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-violet-400" />
                  <h2 className="text-xl md:text-2xl font-bold text-zinc-900 dark:text-white">
                    Report an Issue
                  </h2>
                </div>
              </div>
              <div className="pl-14">
                <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                  If you encounter an accessibility barrier or have suggestions
                  for improvement, we want to hear from you. Please contact us
                  at{" "}
                  <a
                    href="mailto:accessibility@furistic.co"
                    className="text-violet-400 hover:text-violet-300 underline"
                  >
                    accessibility@furistic.co
                  </a>{" "}
                  and we will do our best to respond within 5 business days.
                </p>
              </div>
            </div>
          </div>

          {/* Other Legal Pages */}
          <div className="mt-12 pt-8 border-t border-zinc-800">
            <h3 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider mb-4">
              Other Legal Pages
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                {
                  href: "/legal/terms",
                  label: "Terms of Service",
                  icon: FileText,
                },
                { href: "/legal/privacy", label: "Privacy Policy", icon: Lock },
                {
                  href: "/legal/security",
                  label: "Security & Infrastructure",
                  icon: Lock,
                },
              ].map((link) => {
                const LIcon = link.icon;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="flex items-center gap-3 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-violet-500/30 transition group"
                  >
                    <LIcon className="w-4 h-4 text-violet-400" />
                    <span className="text-sm text-zinc-300 group-hover:text-white transition">
                      {link.label}
                    </span>
                    <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-violet-400 ml-auto transition" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
